import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  LayoutDashboard, ShoppingBag, Shield, Settings, LogOut, 
  Menu, User, ShoppingCart, ChevronDown, Ticket, Package
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import UserNotificationDropdown from './UserNotificationDropdown';

const NavItem = ({ to, icon: Icon, label }) => (
  <NavLink to={to} className={({ isActive }) => `flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all mb-1 ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
    <Icon className="w-5 h-5 mr-3" /> {label}
  </NavLink>
);

const UserLayout = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { user, logout } = useAuth();
  //const navigate = useNavigate();

  // Fetch Live Stats (Cart & Notifications)
  const { data: stats } = useQuery({
    queryKey: ['headerStats'],
    queryFn: () => axiosClient.get('/account/header-stats'),
    refetchInterval: 5000 // Real-time check
  });

  const cartCount = stats?.cart_count || 0;
  //const notifCount = stats?.notification_count || 0;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans">
      
      {/* Sidebar */}
      <aside className={`fixed z-50 h-full w-64 bg-[#0b0f19] text-white transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:static lg:translate-x-0 flex flex-col border-r border-slate-800`}>
        <div className="h-20 flex items-center px-6 border-b border-slate-800">
           <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-white mr-3">A</div>
           <span className="text-lg font-bold tracking-tight">AIRS<span className="text-blue-500">Portal</span></span>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
           <NavItem to="/portal/dashboard" icon={LayoutDashboard} label="Overview" />
           <NavItem to="/portal/shop" icon={Package} label="Buy Plans" />
           <NavItem to="/portal/licenses" icon={Shield} label="My Licenses" />
           <NavItem to="/portal/orders" icon={ShoppingBag} label="Order History" />
           <NavItem to="/portal/tickets" icon={Ticket} label="Support Tickets" />
           <NavItem to="/portal/profile" icon={User} label="My Profile" />
           <NavItem to="/portal/settings" icon={Settings} label="Settings" />
        </nav>
        
        <div className="p-4 border-t border-slate-800 bg-[#0a0e17]">
           <button onClick={logout} className="flex items-center w-full px-4 py-3 text-red-400 hover:bg-white/5 rounded-xl transition-colors text-sm font-medium">
              <LogOut className="w-5 h-5 mr-3"/> Sign Out
           </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-full relative">
         
         {/* ✅ NEW HEADER */}
         <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 shadow-sm flex-shrink-0 z-40 relative">
            <div className="flex items-center">
                <button className="lg:hidden p-2 mr-2 text-slate-600" onClick={() => setIsOpen(!isOpen)}><Menu /></button>
                <h2 className="text-lg font-bold text-slate-800 hidden sm:block">Welcome, {user?.full_name?.split(' ')[0]}</h2>
            </div>

            <div className="flex items-center gap-4">
                
                {/* 🛒 Cart Icon */}
                <button className="relative p-2.5 rounded-full hover:bg-slate-100 text-slate-600 transition-colors">
                    <ShoppingCart className="w-5 h-5" />
                    {cartCount > 0 && (
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-600 rounded-full border border-white animate-pulse"></span>
                    )}
                </button>

                {/* 🔔 Notification Bell */}
                <UserNotificationDropdown />

                {/* 👤 Profile Dropdown */}
                <div className="relative">
                    <button 
                        onClick={() => setProfileOpen(!profileOpen)}
                        className="flex items-center space-x-3 pl-2 pr-3 py-1.5 rounded-full hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all"
                    >
                        <div className="w-9 h-9 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md">
                            {user?.full_name?.[0] || 'U'}
                        </div>
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {profileOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setProfileOpen(false)}></div>
                            <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-xl border border-slate-100 z-20 overflow-hidden animate-in fade-in zoom-in-95 origin-top-right">
                                <div className="px-4 py-3 border-b border-slate-50 bg-slate-50/50">
                                    <p className="text-sm font-bold text-slate-800">{user?.full_name}</p>
                                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                                </div>
                                <div className="p-1">
                                    <Link to="/portal/profile" onClick={() => setProfileOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
                                        <User className="w-4 h-4 mr-2" /> My Profile
                                    </Link>
                                    <Link to="/portal/orders" onClick={() => setProfileOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
                                        <ShoppingBag className="w-4 h-4 mr-2" /> Orders
                                    </Link>
                                    <Link to="/portal/settings" onClick={() => setProfileOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
                                        <Settings className="w-4 h-4 mr-2" /> Settings
                                    </Link>
                                </div>
                                <div className="border-t border-slate-50 p-1">
                                    <button onClick={logout} className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg">
                                        <LogOut className="w-4 h-4 mr-2" /> Sign Out
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

            </div>
         </header>

         {/* Scrollable Body */}
         <main className="flex-1 overflow-y-auto p-4 lg:p-8 relative">
             <div className="max-w-6xl mx-auto pb-10">
                {children}
             </div>
         </main>
      </div>
    </div>
  );
};

export default UserLayout;